import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  const category = useMemo(() => {
    if (!article) return null;
    return categories.find((c) => c.slug === article.category) || null;
  }, [article]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.slug !== article.slug && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="w-full px-4 md:px-6 py-16">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-error-warning-line text-3xl"></i>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">文章未找到</h1>
              <p className="text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被删除。</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xs"></i> 返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '久久在线',
      url: 'https://www.jiujiuzaixian.com',
    },
    url: `https://www.jiujiuzaixian.com/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.jiujiuzaixian.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${article.title} - 久久在线 | 久久在线官网`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.jiujiuzaixian.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                  { label: article.title },
                ]}
              />
            </div>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.publishedAt}</span>
                <span>·</span>
                <span>{article.readTime}阅读</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 久久在线`}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="prose-custom article-content text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{related.publishedAt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}